import { useEffect, useMemo, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Activity, Clock3, Copy, QrCode, Radio, RefreshCw, Smartphone, Wifi, WifiOff } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/PageHeader'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { useReceiverDevices } from '@/lib/queries/useReceiverDevices'
import type { ReceiverDevice } from '@/lib/queries/useReceiverDevices'
import { strapiPost } from '@/lib/strapi'
import { cn } from '@/lib/utils'
import { formatRelative } from '@/lib/utils/date'

type ReceiverLoginQr = {
  code: string
  qrPayload: string
  expiresAt: string
  ttlSeconds: number
}

function secondsUntil(iso: string | null): number {
  if (!iso) return 0
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 1000))
}

function formatTimer(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function shortId(value: string | null | undefined): string {
  if (!value) return 'No device ID'
  return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value
}

function inferDeviceModel(device: ReceiverDevice): string {
  const haystack = `${device.deviceName ?? ''} ${device.connection.userAgent ?? ''}`.toLowerCase()
  if (haystack.includes('t3')) return 'SUNMI T3'
  if (haystack.includes('v2')) return 'SUNMI V2s'
  if (haystack.includes('sunmi')) return 'SUNMI'
  return 'Receiver'
}

function timeLabel(value: string | null | undefined): string {
  if (!value) return 'Never'
  return formatRelative(value)
}

function DeviceOutline({ online, model }: { online: boolean; model: string }) {
  return (
    <div
      className={cn(
        'relative grid h-40 w-28 shrink-0 place-items-center rounded-[2rem] border-2 bg-[var(--color-bg-elevated)] shadow-xl transition-all',
        online
          ? 'border-[var(--color-accent-green)] shadow-[var(--color-accent-green)]/20'
          : 'border-[var(--color-border-default)] shadow-black/10',
      )}
    >
      <div className="absolute top-3 h-1.5 w-10 rounded-full bg-[var(--color-border-strong)]" />
      <div className="absolute inset-x-3 top-7 bottom-6 rounded-[1.25rem] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2 py-3">
        <div className={cn('mb-2 h-2 w-2 rounded-full', online ? 'bg-[var(--color-accent-green)]' : 'bg-[var(--color-text-muted)]')} />
        <div className="space-y-1.5">
          <div className="h-2 rounded-full bg-[var(--color-border-subtle)]" />
          <div className="h-2 w-10 rounded-full bg-[var(--color-border-subtle)]" />
          <div className={cn('mt-3 h-8 rounded-md', online ? 'bg-[var(--color-accent-green-dim)]' : 'bg-[var(--color-bg-overlay)]')} />
        </div>
      </div>
      <div className="absolute bottom-2 h-2 w-2 rounded-full border border-[var(--color-border-strong)]" />
      <span className="absolute -bottom-7 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
        {model}
      </span>
      {online && <span className="absolute -right-1 top-8 h-3 w-3 animate-ping rounded-full bg-[var(--color-accent-green)]" />}
    </div>
  )
}

function DeviceTile({ device }: { device: ReceiverDevice }) {
  const online = device.connection.online
  const platform = device.connection.platform ?? device.platform ?? 'unknown'
  const version = device.connection.appVersion ?? device.appVersion ?? 'unknown'

  return (
    <div
      className={cn(
        'grid gap-5 rounded-[var(--radius-md)] border bg-[var(--color-bg-surface)] p-5 transition-all sm:grid-cols-[128px_1fr]',
        online
          ? 'border-[var(--color-accent-green)]/55 shadow-lg shadow-[var(--color-accent-green)]/10'
          : 'border-[var(--color-border-subtle)]',
      )}
    >
      <div className="flex justify-center pb-7 sm:pb-0">
        <DeviceOutline online={online} model={inferDeviceModel(device)} />
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-[var(--color-text-primary)]">
              {device.deviceName || 'Unnamed receiver'}
            </p>
            <p className="mt-1 font-mono text-xs text-[var(--color-text-muted)]">{shortId(device.deviceId)}</p>
          </div>
          <Badge variant={online ? 'green' : 'default'} className="gap-1.5">
            {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {online ? 'Online' : 'Offline'}
          </Badge>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">Restaurant</p>
            <p className="mt-1 truncate text-sm text-[var(--color-text-secondary)]">{device.restaurant?.name ?? 'Unknown'}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">Version</p>
            <p className="mt-1 truncate text-sm text-[var(--color-text-secondary)]">{platform} app {version}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">Last seen</p>
            <p className="mt-1 truncate text-sm text-[var(--color-text-secondary)]">{timeLabel(device.lastSeenAt)}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-text-muted)]">Connection</p>
            <p className="mt-1 truncate text-sm text-[var(--color-text-secondary)]">
              {online ? `Live since ${timeLabel(device.connection.connectedAt)}` : 'Waiting for socket'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function OrdevoNetworkPage() {
  const [qr, setQr] = useState<ReceiverLoginQr | null>(null)
  const [loading, setLoading] = useState(true)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const devicesQuery = useReceiverDevices()
  const deviceReport = devicesQuery.data?.data
  const devices = deviceReport?.devices ?? []
  const summary = deviceReport?.summary ?? { total: 0, online: 0, offline: 0 }
  const highlightedDevice = devices.find((device) => device.connection.online) ?? devices[0] ?? null
  const expired = secondsLeft <= 0
  const statusLabel = useMemo(() => {
    if (loading) return 'Preparing pairing code'
    if (!qr) return 'Pairing unavailable'
    if (expired) return 'QR expired'
    return `Expires in ${formatTimer(secondsLeft)}`
  }, [expired, loading, qr, secondsLeft])

  async function loadQr() {
    setLoading(true)
    try {
      const res = await strapiPost<ReceiverLoginQr>('/api/receiver-login/qr', {})
      setQr(res.data)
      setSecondsLeft(secondsUntil(res.data.expiresAt))
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to create receiver login QR')
      setQr(null)
      setSecondsLeft(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadQr()
  }, [])

  useEffect(() => {
    const id = window.setInterval(() => {
      setSecondsLeft(secondsUntil(qr?.expiresAt ?? null))
    }, 1000)
    return () => window.clearInterval(id)
  }, [qr?.expiresAt])

  useEffect(() => {
    if (!qr || secondsLeft !== 15) return
    toast.message('Receiver login QR expires soon')
  }, [qr, secondsLeft])

  async function copyPayload() {
    if (!qr?.qrPayload) return
    await navigator.clipboard.writeText(qr.qrPayload)
    toast.success('Pairing payload copied')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ordevo Network"
        description="Pair, monitor, and expand the devices connected to this restaurant."
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => void devicesQuery.refetch()} loading={devicesQuery.isFetching}>
              <Activity className="h-4 w-4" />
              Sync
            </Button>
            <Button variant="secondary" onClick={() => void loadQr()} loading={loading}>
              <RefreshCw className="h-4 w-4" />
              New QR
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(320px,420px)_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-[var(--color-accent-amber)]" />
              Receiver login QR
            </CardTitle>
            <CardDescription>Scan from the receiver app to pair a device.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-white p-4">
                {qr?.qrPayload && !expired ? (
                  <QRCodeSVG value={qr.qrPayload} size={256} level="M" includeMargin />
                ) : (
                  <div className="flex h-64 w-64 items-center justify-center text-center text-sm font-medium text-slate-500">
                    {loading ? 'Creating QR...' : 'Refresh to create a new QR'}
                  </div>
                )}
              </div>

              <div className="flex w-full items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-2">
                <span className="text-sm text-[var(--color-text-secondary)]">{statusLabel}</span>
                <span className="h-2.5 w-2.5 rounded-full bg-[var(--color-accent-amber)]" />
              </div>

              <div className="grid w-full grid-cols-2 gap-2">
                <Button variant="secondary" onClick={copyPayload} disabled={!qr?.qrPayload}>
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
                <Button onClick={() => void loadQr()} loading={loading}>
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Network status</CardTitle>
            <CardDescription>{summary.online} online of {summary.total} paired devices.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-5 xl:grid-cols-[180px_1fr]">
              <div className="flex justify-center pb-8 xl:pb-0">
                {highlightedDevice ? (
                  <DeviceOutline online={highlightedDevice.connection.online} model={inferDeviceModel(highlightedDevice)} />
                ) : (
                  <div className="grid h-40 w-28 place-items-center rounded-[2rem] border-2 border-dashed border-[var(--color-border-default)] text-[var(--color-text-muted)]">
                    <Smartphone className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] p-4">
                  <Radio className="mb-3 h-5 w-5 text-[var(--color-accent-green)]" />
                  <p className="text-2xl font-semibold text-[var(--color-text-primary)]">{summary.online}</p>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">Connected now</p>
                </div>
                <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] p-4">
                  <Smartphone className="mb-3 h-5 w-5 text-[var(--color-accent-amber)]" />
                  <p className="text-2xl font-semibold text-[var(--color-text-primary)]">{summary.total}</p>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">Paired devices</p>
                </div>
                <div className="rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] p-4">
                  <Clock3 className="mb-3 h-5 w-5 text-[var(--color-accent-blue)]" />
                  <p className="text-2xl font-semibold text-[var(--color-text-primary)]">{summary.offline}</p>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">Offline</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connected devices</CardTitle>
          <CardDescription>Receiver devices registered to this restaurant account.</CardDescription>
        </CardHeader>
        <CardContent>
          {devicesQuery.isLoading ? (
            <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-border-subtle)] py-12 text-center text-sm text-[var(--color-text-muted)]">
              Loading devices...
            </div>
          ) : devicesQuery.isError ? (
            <div className="rounded-[var(--radius-md)] border border-[var(--color-accent-red)]/35 bg-[var(--color-accent-red-dim)] px-4 py-3 text-sm text-[var(--color-accent-red)]">
              Could not load connected devices.
            </div>
          ) : devices.length === 0 ? (
            <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-border-subtle)] py-12 text-center text-sm text-[var(--color-text-muted)]">
              No receiver devices have paired yet.
            </div>
          ) : (
            <div className="grid gap-4">
              {devices.map((device) => (
                <DeviceTile key={device.documentId} device={device} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
